import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { ZitaLogo, ZitaWordmark } from '@/components/ZitaLogo';
import { PublicFooter } from '@/components/PublicFooter';
import { SquareCardForm } from '@/components/SquareCardForm';
import { AUTH } from '@/constants/testIds';
import { Check, LogOut, Lock, AlertTriangle, Sparkles } from 'lucide-react';

const MAIN_FEATURES = [
  'Every supported public source and search engine — no artificial tier gates',
  'Ari — natural-language chat, voice, and TTS with tool execution',
  'Persistent search intelligence, Attention Reduction, Trust Intelligence',
  'Deep enrichment, cross-scan comparison, review-state memory',
  'Unlimited folders + Save All Matches + Excel/CSV/PDF/Google Sheets exports',
  'Scheduled monitoring with delta detection and honest source status',
  'Mobile + desktop, no ads, cancel anytime',
];

/**
 * /subscribe — post-trial paywall (Feb 2026, now Square-backed).
 *
 * Shown when a signed-in user has no active access:
 *   - trial_expired  → 3-day free trial ended
 *   - payment_failed → recurring charge failed, must update card
 *   - canceled_ended → cancellation took effect, must re-subscribe
 *   - none           → legacy account, never subscribed
 *
 * Aug 2026 doctrine consolidation — Ari Search+ has been retired as a
 * separate paid tier. Every capability formerly gated behind the $9.99
 * add-on is now included in the flat $29.99 Zita Pro membership.
 */
const Subscribe = () => {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [square, setSquare] = useState(null); // { application_id, location_id, configured }

  // If the user IS active (e.g. refreshed after paying), bounce home.
  useEffect(() => {
    if (user?.has_active_access) navigate('/', { replace: true });
  }, [user, navigate]);

  // Pull the Square handoff config once
  useEffect(() => {
    api.get('/billing/plans').then((r) => setSquare(r.data?.square || null)).catch(() => {});
  }, []);

  const handleToken = async (token, cardholderName) => {
    setBusy(true);
    try {
      // 1. Attach the tokenized card to a Square customer
      await api.post('/billing/attach-card', {
        token,
        cardholder_name: cardholderName || null,
      });
      // 2. Create the Zita Pro subscription (Ari Search+ retired — no add-on).
      await api.post('/billing/activate', {
        include_main: true,
        include_addon: false,
      });
      toast.success('Subscription active. Welcome to Zita Pro.');
      await refresh();
      navigate('/');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === 'string' && detail
          ? detail
          : 'Could not activate your subscription. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  const signOut = async () => {
    await logout();
    navigate('/login');
  };

  if (!user) return null;

  const isPaymentFailed = user.access_reason === 'payment_failed';
  const isTrialExpired = user.access_reason === 'trial_expired';
  const isCanceledEnded = user.access_reason === 'canceled_ended';

  const banner = isPaymentFailed
    ? { color: 'bg-red-50 text-red-800 border-red-200', title: 'Payment failed — update your card to continue.', icon: AlertTriangle }
    : isCanceledEnded
      ? { color: 'bg-amber-50 text-amber-900 border-amber-200', title: 'Your subscription has ended. Re-subscribe to continue.', icon: Lock }
      : isTrialExpired
        ? { color: 'bg-amber-50 text-amber-900 border-amber-200', title: 'Your 3-day free trial has ended.', icon: Lock }
        : { color: 'bg-zita-sand/60 text-zita-espresso border-zita-taupe', title: 'Subscribe to unlock Zita.', icon: Lock };

  const BannerIcon = banner.icon;
  const total = 29.99;
  const submitLabel = `Subscribe · $29.99 / month`;

  return (
    <div className="min-h-screen flex flex-col bg-zita-cream" data-testid="subscribe-page">
      <header className="border-b border-zita-taupe/40 bg-zita-cream/80 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ZitaLogo size={30} />
            <ZitaWordmark className="text-xl" />
          </div>
          <button
            type="button"
            onClick={signOut}
            data-testid="subscribe-signout"
            className="text-xs text-zita-mocha hover:text-zita-cocoa inline-flex items-center gap-1"
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-2xl space-y-6">
          <div
            className={`flex items-center gap-3 border rounded-lg px-4 py-3 ${banner.color}`}
            data-testid="subscribe-banner"
          >
            <BannerIcon size={18} className="shrink-0" />
            <div className="text-sm font-semibold" data-testid="subscribe-banner-title">
              {banner.title}
            </div>
          </div>

          <div className="zita-card p-8 space-y-6">
            <div className="flex items-baseline justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.16em] text-zita-mocha">Membership</div>
                <h1 className="font-serif text-4xl text-zita-espresso mt-1">Zita Monthly</h1>
                <p className="text-sm text-zita-mocha mt-1">
                  {user.email} — pick up where you left off.
                </p>
              </div>
              <div className="text-right">
                <div className="font-serif text-5xl text-zita-cocoa" data-testid="subscribe-price-main">$29.99</div>
                <div className="text-xs text-zita-muted">per month · cancel anytime</div>
              </div>
            </div>

            <ul className="space-y-2 text-sm text-zita-mocha">
              {MAIN_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check size={16} className="mt-0.5 text-zita-cocoa shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            {/* Total */}
            <div
              className="flex items-center justify-between border-t border-zita-taupe/40 pt-4"
              data-testid="subscribe-total-row"
            >
              <div className="text-xs uppercase tracking-wider text-zita-mocha">You&apos;ll be charged today</div>
              <div className="text-right">
                <div className="font-serif text-2xl text-zita-espresso" data-testid="subscribe-total">
                  $29.99 / month
                </div>
                <div className="text-[11px] text-zita-muted" data-testid="subscribe-total-detail">Everything included · no add-ons</div>
              </div>
            </div>

            {!square?.configured ? (
              <div
                className="bg-amber-50 text-amber-900 border border-amber-200 rounded-lg px-4 py-3 text-sm"
                data-testid="subscribe-square-unconfigured"
              >
                Billing is temporarily unavailable while our payment provider is being set up.
                Please check back shortly or contact support.
              </div>
            ) : (
              <SquareCardForm
                applicationId={square.application_id}
                locationId={square.location_id}
                submitLabel={submitLabel}
                cardholderName={user.display_name || ''}
                busy={busy}
                onToken={handleToken}
              />
            )}

            <div className="text-[11px] text-zita-muted text-center leading-relaxed">
              Payments powered by Square. Automatic monthly billing enabled by default.
              You can cancel anytime in Settings → Subscription. Access remains until the
              end of your current billing period — no partial or prorated refunds. Every
              Zita Pro capability is included; there are no separate add-ons.
            </div>
          </div>

          {/* Keep testids in DOM so any older test still resolves them */}
          <button data-testid={AUTH.subscribeCta} className="hidden" aria-hidden="true" />
          <button data-testid={AUTH.subscribeUpdateCardCta} className="hidden" aria-hidden="true" />
        </div>
      </main>

      <PublicFooter />
    </div>
  );
};

export default Subscribe;
